import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { FaFileInvoiceDollar, FaPlus, FaTrash, FaTimes } from "react-icons/fa";

const D2LenderPlans = () => {
  const [plans, setPlans] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    lenderId: "",
    lenderName: "",
    name: "",
    apr: 6.90,
    term: 72,
    creditRequirements: "Tier 1 - 3 Prime",
    vehicleRules: "New & Used Eligible",
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      
      const [planRes, lenderRes] = await Promise.all([
        fetch(`${getBackendBaseUrl()}/d2-lenders/plans`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${getBackendBaseUrl()}/d2-lenders`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const planData = await planRes.json();
      const lenderData = await lenderRes.json();
      
      if (planData.success) setPlans(planData.data || []);
      if (lenderData.success) {
        setLenders(lenderData.data || []);
        if (lenderData.data?.length > 0 && !formData.lenderId) {
          setFormData(prev => ({ 
            ...prev, 
            lenderId: lenderData.data[0]._id || lenderData.data[0].lenderId,
            lenderName: lenderData.data[0].name
          }));
        }
      }
    } catch (err) {
      console.error("Failed to fetch lender plans data", err);
      toast.error("Failed to load lender plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "lenderId") {
      const selected = lenders.find(l => (l._id === value || l.lenderId === value));
      setFormData(prev => ({ 
        ...prev, 
        lenderId: value, 
        lenderName: selected?.name || prev.lenderName 
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-lenders/plans`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Lender plan added successfully!");
        setShowModal(false);
        setFormData({
          lenderId: lenders[0]?._id || "",
          lenderName: lenders[0]?.name || "",
          name: "",
          apr: 6.90,
          term: 72,
          creditRequirements: "Tier 1 - 3 Prime",
          vehicleRules: "New & Used Eligible",
        });
        fetchData();
      } else {
        toast.error(data.message || "Failed to add plan");
      }
    } catch (err) {
      console.error("Error saving plan", err);
      toast.error("Failed to add plan");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-lenders/plans/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Plan deleted");
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to delete plan");
    }
  };

  const resolveLenderName = (plan) => {
    if (plan.lenderName) return plan.lenderName;
    if (plan.lenderId?.name) return plan.lenderId.name;
    const match = lenders.find(l => l._id === plan.lenderId || l.lenderId === plan.lenderId);
    if (match) return match.name;
    return "Toyota Financial Services";
  };

  const resolveTerm = (plan) => {
    return plan.term || plan.termMonths || plan.term_months || 72;
  };

  return (
    <div className="p-8 h-full w-full bg-slate-50/50 flex flex-col overflow-y-auto animate-fade-in-up">
      {/* Title Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
            <span className="p-2.5 bg-purple-50 text-purple-600 rounded-2xl shadow-sm border border-purple-100">
              <FaFileInvoiceDollar size={24} />
            </span>
            Lender Plans
          </h1>
          <p className="text-slate-500 text-xs mt-1 leading-relaxed">
            Configure financing programs, define rates, structural terms, guidelines, and underwriting criteria.
          </p>
        </div>
        {user?.role !== "Salesperson" && (
          <button
            onClick={() => setShowModal(true)}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 shadow-md transition-all duration-200 flex items-center gap-2 text-sm cursor-pointer"
          >
            <FaPlus size={12} /> Add Lender Plan
          </button>
        )}
      </div>

      {/* Lender Plans Table Container */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200/80 text-slate-500 text-xs font-bold uppercase tracking-wider">
                <th className="px-6 py-4">Plan Name</th>
                <th className="px-6 py-4">Lender</th>
                <th className="px-6 py-4">APR (%)</th>
                <th className="px-6 py-4">Term (Months)</th>
                <th className="px-6 py-4">Credit Tier</th>
                <th className="px-6 py-4">Vehicle Eligibility</th>
                {user?.role !== "Salesperson" && <th className="px-6 py-4 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={user?.role === "Salesperson" ? "6" : "7"} className="px-6 py-10 text-center text-slate-400 text-sm">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></span>
                      Loading plans...
                    </div>
                  </td>
                </tr>
              ) : plans.length === 0 ? (
                <tr>
                  <td colSpan={user?.role === "Salesperson" ? "6" : "7"} className="px-6 py-12 text-center text-slate-400 text-sm italic">
                    No lender plans found. Click "Add Lender Plan" to create one.
                  </td>
                </tr>
              ) : (
                plans.map((plan) => (
                  <tr key={plan._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 font-bold text-slate-800 text-sm">{plan.name}</td>
                    <td className="px-6 py-4 text-sm font-bold text-indigo-700">
                      {resolveLenderName(plan)}
                    </td>
                    <td className="px-6 py-4 text-sm font-black text-blue-600">{plan.apr}%</td>
                    <td className="px-6 py-4 text-sm text-slate-700 font-bold">{resolveTerm(plan)} mos</td>
                    <td className="px-6 py-4 text-xs font-semibold text-slate-600">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md border border-blue-100 font-bold">
                        {plan.creditTier || plan.creditRequirements || "Tier 1 Prime"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-500 font-medium">
                      {plan.vehicleEligibility || plan.vehicleRules || "New & Used Eligible"}
                    </td>
                    {user?.role !== "Salesperson" && (
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDelete(plan._id)}
                          className="text-red-500 hover:text-red-700 font-semibold text-xs tracking-wide bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors border border-red-200/30 flex items-center gap-1.5 ml-auto cursor-pointer"
                        >
                          <FaTrash size={10} /> Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* True Fullscreen Modal using ReactDOM.createPortal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 animate-fade-in-up">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-8 w-full max-w-lg relative">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
            >
              <FaTimes size={16} />
            </button>
            
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight mb-1">Add Lender Financing Plan</h2>
            <p className="text-slate-400 text-xs mb-6 border-b pb-4">
              Configure interest rate, terms, and guidelines for institutional lender programs.
            </p>
            
            <form onSubmit={handleSavePlan} className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Lender Partner *</label>
                <select
                  name="lenderId"
                  value={formData.lenderId}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white focus:border-indigo-600 outline-none"
                >
                  {lenders.map((lender) => (
                    <option key={lender._id || lender.lenderId} value={lender._id || lender.lenderId}>
                      {lender.name} ({lender.type || "Major Bank"})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Plan Name *</label>
                <input
                  required
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none"
                  placeholder="e.g. Dealer Partner 72"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase mb-1">APR Rate (%) *</label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    name="apr"
                    value={formData.apr}
                    onChange={handleInputChange}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none"
                    placeholder="6.90"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Term (Months) *</label>
                  <input
                    required
                    type="number"
                    name="term"
                    value={formData.term}
                    onChange={handleInputChange}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none"
                    placeholder="72"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Credit Tier Requirement</label>
                  <input
                    type="text"
                    name="creditRequirements"
                    value={formData.creditRequirements}
                    onChange={handleInputChange}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none"
                    placeholder="Tier 1 - 3 Prime"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Vehicle Eligibility</label>
                  <input
                    type="text"
                    name="vehicleRules"
                    value={formData.vehicleRules}
                    onChange={handleInputChange}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none"
                    placeholder="New & Used Eligible"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6 border-t pt-5">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 font-semibold text-sm transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl text-white font-semibold hover:shadow-lg shadow-md transition-all text-sm cursor-pointer"
                >
                  Save Plan
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default D2LenderPlans;
